import { useEffect, useCallback, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';

interface ScanQueueItem {
  id: string;
  template_id: string;
  template_name: string;
  field_data: Record<string, string>;
  created_at: string;
}

interface UseScanQueueOptions {
  onScanReceived?: (item: ScanQueueItem) => void;
  enabled?: boolean;
}

/**
 * Listens for incoming scanned labels from the scan_queue table via realtime.
 * Returns a list of pending items for review.
 */
export function useScanQueue({ onScanReceived, enabled = true }: UseScanQueueOptions) {
  const { user } = useAuth();
  const [pendingScans, setPendingScans] = useState<ScanQueueItem[]>([]);

  // Load existing pending items on mount
  useEffect(() => {
    if (!enabled || !user) return;
    const loadPending = async () => {
      const { data } = await supabase
        .from('scan_queue' as any)
        .select('id, template_id, template_name, field_data, created_at')
        .eq('user_id', user.id)
        .eq('status', 'pending')
        .order('created_at', { ascending: false })
        .limit(50);
      if (data) {
        setPendingScans(data as any as ScanQueueItem[]);
      }
    };
    loadPending();
  }, [enabled, user]);

  const processEntry = useCallback((payload: any) => {
    const record = payload.new;
    if (!record || record.status !== 'pending') return;

    const item: ScanQueueItem = {
      id: record.id,
      template_id: record.template_id,
      template_name: record.template_name || 'Unknown',
      field_data: record.field_data || {},
      created_at: record.created_at,
    };

    setPendingScans(prev => [item, ...prev]);
    onScanReceived?.(item);
    toast.info(`📱 Scan received: ${item.template_name}`);
  }, [onScanReceived]);

  useEffect(() => {
    if (!enabled || !user) return;

    const channel = supabase
      .channel('scan-queue-listener')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'scan_queue',
          filter: `user_id=eq.${user.id}`,
        },
        processEntry
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [enabled, user, processEntry]);

  const dismissScan = useCallback(async (id: string) => {
    await supabase
      .from('scan_queue' as any)
      .update({ status: 'processed' } as any)
      .eq('id', id);
    setPendingScans(prev => prev.filter(s => s.id !== id));
  }, []);

  const applyAndDismiss = useCallback(async (id: string) => {
    await supabase
      .from('scan_queue' as any)
      .update({ status: 'applied' } as any)
      .eq('id', id);
    setPendingScans(prev => prev.filter(s => s.id !== id));
  }, []);

  return { pendingScans, dismissScan, applyAndDismiss };
}

export type { ScanQueueItem };
